import { useState } from 'react';

import { DEVICE_TYPES, VOWIFI_VERSIONS } from '../../../../../constants';
import { isValid } from '../../../../../helpers/common';
import { parseError } from '../../../../../helpers/errorUtils';
import { tryCatch } from '../../../../../helpers/servicesHelper';
import { useClassHelper, useUpdateEffect } from '../../../../../hooks';
import {
  IServices,
  KeyOf,
  Maybe,
  SelectedFieldInPreview,
} from '../../../../../types';
import { IconRenderer } from '../../../../IconRenderer';
import { Stack } from '../../../../Stack';
import { DATADOG_ACTIONS } from '../../../constants';
import { useDataDogActions } from '../../../hooks/useDataDogActions';
import {
  componentName,
  ERROR_CODES,
  LOCALIZATION_ERRORS_MAP,
} from '../constants';
import { useAddressErrorState } from '../hooks/useAddressErrorState';
import { useFormState } from '../hooks/useFormState';
import { useResetScrollOnMount } from '../hooks/useResetScrollOnMount';
import useVoWifiActivationMutation from '../hooks/useVoWifiMutation';
import { useVoWifiRenderTracking } from '../hooks/useVoWifiRenderTracking';
import { PageProps, VoWifiConfirmation } from '../types';
import { ActionSection } from './ActionSection';
import { ErrorTemplate } from './ErrorTemplate';
import { HeaderImage } from './shared/HeaderImage';
import { Paragraph } from './shared/Paragraph';
import { Title } from './shared/Title';

const statusFields = {
  text: ['title', 'subtitle', 'confirm', 'editAddress', 'close'],
  success: ['success_title', 'success_subtitle', 'done'],
  error: [
    'error_title',
    'error_subtitle',
    'emergency_address_validation_failed',
    'address_help_label',
  ],
  loading: ['loading_title', 'loading_subtitle'],
};

// Portal-preview support: selecting a PSAP copy field renders the rejection
// face with this fixture, since no carrier 422 can happen in the preview.
const PSAP_PREVIEW_FIELDS: string[] = [
  'emergency_address_validation_failed',
  'address_help_label',
];
const PREVIEW_PSAP_DETAILS =
  'That postal code does not match the given city (Halifax, Nova Scotia). Did you mean Dartmouth, Nova Scotia?';

const defaultTexts = {
  title: 'Confirm Your Address',
  subtitle:
    'The following address is registered as your Wi-Fi Calling emergency 911 address',
  confirm: 'Confirm',
  editAddress: 'Edit Address',
  close: 'Close',
  done: 'Done',
  cancel: 'Cancel',
} as const;

interface Props extends PageProps, Partial<Omit<VoWifiConfirmation, 'type'>> {
  context?: 'default' | 'success' | 'error' | 'loading';
  selectedField?: Maybe<SelectedFieldInPreview>;
  services: IServices;
  state: string;
  isPortalTest: boolean;
  portalMessenger: Maybe<any>;
  deviceSdk: Maybe<any>;
  deviceType?: (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];
}

const statusTypes = {
  pending: 'pending',
  success: 'success',
  error: 'error',
} as const;

export const ConfirmationAddress = ({
  themeValues,
  state,
  isPortalPreview,
  title,
  subtitle,
  confirm,
  editAddress,
  selectedField,
  success_title,
  success_subtitle,
  error_title,
  error_subtitle,
  loading_title,
  loading_subtitle,
  services,
  isPortalTest,
  portalMessenger,
  deviceSdk,
  deviceType,
  done,
  close,
  emergency_address_validation_failed,
  address_help_label,
}: Props) => {
  const c = useClassHelper(componentName);
  const { formState } = useFormState();
  const [status, setStatus] = useState<Maybe<KeyOf<typeof statusTypes>>>(null);
  const [provisioningStatus, setProvisioningStatus] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<Maybe<string>>(null);
  const [isAddressError, setIsAddressError] = useState(false);
  const { addressErrorDetails, setAddressErrorDetails } =
    useAddressErrorState();
  // Portal preview only: fixture carrier feedback while a PSAP copy field is
  // selected. Runtime always reads the shared store.
  const [previewErrorDetails, setPreviewErrorDetails] =
    useState<Maybe<string>>(null);
  const activeErrorDetails = previewErrorDetails ?? addressErrorDetails;
  const country = themeValues?.configuration_settings?.country;
  // New (autocomplete) flow renders the Figma 2454-845 variants of this
  // screen; the legacy flow is untouched.
  const autocompleteEnabled = isValid(
    themeValues?.configuration_settings?.emergency_address_autocomplete_enabled,
  );

  // Reset scroll on mount
  useResetScrollOnMount();

  // Get DataDog actions (undefined in preview mode)
  const { dataDogLogger, actions } = useDataDogActions({
    isPortalPreview,
    actionKey: 'vowifi',
  });

  const { containerRef, onImageError } = useVoWifiRenderTracking(
    isPortalPreview,
    'confirmation',
  );

  const { mutateAsync: activateVoWifi, isPending } =
    useVoWifiActivationMutation(services);

  useUpdateEffect(() => {
    if (selectedField) {
      const { type, field } = selectedField;

      if (type === VOWIFI_VERSIONS['vowifi-confirmation']) {
        const previewingPsap =
          Boolean(isPortalPreview) && PSAP_PREVIEW_FIELDS.includes(field);
        setPreviewErrorDetails(previewingPsap ? PREVIEW_PSAP_DETAILS : null);

        switch (true) {
          case statusFields.success.includes(field): {
            setStatus(statusTypes.success);
            break;
          }
          case statusFields.error.includes(field): {
            setStatus(statusTypes.error);
            setErrorMessage(
              LOCALIZATION_ERRORS_MAP[
                field as keyof typeof LOCALIZATION_ERRORS_MAP
              ],
            );
            setIsAddressError(PSAP_PREVIEW_FIELDS.includes(field));
            break;
          }
          case statusFields.loading.includes(field): {
            setStatus(statusTypes.pending);
            break;
          }
          case statusFields.text.includes(field): {
            setStatus(null);
            break;
          }
        }
      }
    }
  }, [selectedField]);

  // Tracks when the user actually sees the success screen, separate from
  // confirmation_success which fires when the activation API returns. A render
  // bug could set status to success without the screen mounting — this catches
  // that gap.
  useUpdateEffect(() => {
    if (status === statusTypes.success && !isPortalPreview) {
      dataDogLogger.log(actions?.success_screen_shown, {
        flow: 'activate',
      });
    }
  }, [status]);

  const isLoading = status === statusTypes.pending || isPending;

  const handleSubmit = async () => {
    if (isPortalPreview) {
      return;
    }

    if (status === statusTypes.success) {
      const { error: activationError } = await tryCatch(
        deviceSdk.voWifiActivation(provisioningStatus),
      );

      if (activationError) {
        const { errorCode, errorMessage, statusCode } =
          parseError(activationError);

        dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
          flow: 'activate on voWifiActivation',
          statusCode,
          errorCode: errorCode ?? 'UNKNOWN',
          errorMessage,
        });
      }

      return;
    }

    if (status === statusTypes.error) {
      if (isPortalTest) {
        window.close();
        return;
      }

      return;
    }

    setStatus(statusTypes.pending);

    // Mark the activation start so DataDog can derive submit→success latency.
    dataDogLogger.addTiming(DATADOG_ACTIONS.timings.vowifi_submitted);

    // street_number is optional on the carrier API — omit it when the form
    // never collected one (autocomplete off) instead of sending ''.
    const { street_number, ...addressFields } = formState;
    const emergencyAddress = {
      ...(street_number ? { street_number } : {}),
      ...addressFields,
      country,
    };

    const { error, data } = await tryCatch(
      activateVoWifi(
        deviceType === 'android'
          ? {
              ...emergencyAddress,
              display_name: '',
              sip_username: '',
              tc_accepted: 'accepted',
              state,
            }
          : {
              emergency_address: emergencyAddress,
              tc_accepted: true,
              state,
            },
      ),
    );

    if (data && data?.provisioning_status === 'enabled') {
      setProvisioningStatus(true);
    }

    if (error) {
      const { errorCode, errorMessage, errorDetails, statusCode } =
        parseError(error);

      // PSAP rejected the address (UES-11807). The ES agents remap the
      // carrier's 422 to HTTP 400 + this error_code, so classify on the code
      // only — a raw 422 reaching the FE is a FastAPI request-validation
      // error and must NOT offer "Edit Address" as the recovery.
      // `error_details` is shown verbatim (here and on the Address screen).
      const addressError =
        errorCode === ERROR_CODES.EMERGENCY_ADDRESS_VALIDATION_FAILED;

      setStatus(statusTypes.error);
      setErrorMessage(errorCode);
      setIsAddressError(addressError);
      setAddressErrorDetails(
        addressError && errorDetails ? errorDetails : null,
      );

      dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
        flow: 'activate on activateVoWifi',
        statusCode,
        errorCode: errorCode ?? 'UNKNOWN',
        errorMessage,
        errorDetails,
      });

      if (isPortalTest) {
        portalMessenger.propagateError(errorMessage);
      }

      return;
    }

    if (isPortalTest) {
      portalMessenger.propagateSuccess();
    }

    // --- Success signals ---
    // succeeded − submitted = activation latency. Session stays LIVE; it closes
    // on the Done handoff (voWifiActivation → stopSession).
    dataDogLogger.log(actions?.confirmation_success, {
      context: 'vowifi',
    });
    dataDogLogger.addTiming(DATADOG_ACTIONS.timings.vowifi_succeeded);
    dataDogLogger.addGlobal('completed', true);
    dataDogLogger.log(DATADOG_ACTIONS.success.template_success, {
      flow: 'activate',
      // Distinguishes a full activation ('enabled') from a partial success.
      provisioningStatus: data?.provisioning_status,
    });

    setAddressErrorDetails(null);
    setStatus(statusTypes.success);
  };

  const renderPageTitle = () => {
    const titlesMap = {
      [statusTypes.pending]: loading_title ? loading_title : 'Activating...',
      [statusTypes.success]: success_title
        ? success_title
        : 'Wi-Fi Calling Activated',
      [statusTypes.error]: error_title
        ? error_title
        : autocompleteEnabled
        ? 'An Error Occurred'
        : 'An error occurred',
    } as const;

    const text = titlesMap[status] ?? (title ? title : defaultTexts.title);
    return text;
  };

  const renderSubTitle = () => {
    const textMap = {
      [statusTypes.pending]: loading_subtitle
        ? loading_subtitle
        : 'Please wait while we activate Wifi Calling',
      [statusTypes.success]: success_subtitle
        ? success_subtitle
        : autocompleteEnabled
        ? 'Emergency 911 address registered.'
        : 'Wi-Fi Calling is being enabled on your phone.',
      [statusTypes.error]: (
        <ErrorTemplate
          errorCode={errorMessage}
          localizationMap={{
            vowifi_error_activation_failed: error_subtitle,
            emergency_address_validation_failed:
              emergency_address_validation_failed ||
              (autocompleteEnabled
                ? 'Invalid address details. Please try again.'
                : ''),
            error_subtitle,
          }}
        />
      ),
    } as const;

    const text =
      textMap[status] ?? (subtitle ? subtitle : defaultTexts.subtitle);

    return (
      <>
        <Paragraph fontFamily={themeValues.fontParagraph} fontWeight="bold">
          {text}
        </Paragraph>
        {status === statusTypes.error &&
          activeErrorDetails &&
          (autocompleteEnabled ? (
            <Stack gap={2} direction="column">
              <Paragraph
                fontFamily={themeValues.fontParagraph}
                fontWeight="bold"
                className={c('__error-help-label')}
                data-testid="vowifi-error-help-label"
              >
                {`${
                  address_help_label?.trim()
                    ? address_help_label
                    : 'Address Help'
                }:`}
              </Paragraph>
              <div className={c('__error-help-box')}>
                <Paragraph
                  fontFamily={themeValues.fontParagraph}
                  data-testid="vowifi-error-details"
                >
                  {activeErrorDetails}
                </Paragraph>
              </div>
            </Stack>
          ) : (
            <Paragraph
              fontFamily={themeValues.fontParagraph}
              className={c('__error-details')}
              data-testid="vowifi-error-details"
            >
              {activeErrorDetails}
            </Paragraph>
          ))}
      </>
    );
  };

  const renderBody = () => {
    if (isPortalPreview) {
      return (
        <div className={c('__confirmation-screen-body')}>
          <Stack className="gap-[3px]!" direction="column">
            <Paragraph fontFamily={themeValues.fontParagraph}>
              Address Line 1
            </Paragraph>
            <Paragraph fontFamily={themeValues.fontParagraph}>
              Address Line 2 (Optional)
            </Paragraph>
            <Paragraph fontFamily={themeValues.fontParagraph}>City</Paragraph>
            <Paragraph fontFamily={themeValues.fontParagraph}>
              State/Province
            </Paragraph>
            <Paragraph fontFamily={themeValues.fontParagraph}>
              Zip/Posta code
            </Paragraph>
          </Stack>
        </div>
      );
    }

    const streetLine = [formState.street_number, formState.address_line_1]
      .filter(Boolean)
      .join(' ');

    if (
      formState.street_number ||
      formState.address_line_1 ||
      formState.address_line_2 ||
      formState.city ||
      formState.state_province_region ||
      formState.zip_postal_code
    ) {
      if (autocompleteEnabled) {
        // Figma 2454-921: "7 - 123 Fake Street" / "City, PROV" / postal in a
        // bordered panel, normal case.
        const aptDashStreet = formState.address_line_2
          ? `${formState.address_line_2} - ${streetLine}`
          : streetLine;
        const cityLine = [formState.city, formState.state_province_region]
          .filter(Boolean)
          .join(', ');
        const lines = [aptDashStreet, cityLine, formState.zip_postal_code];

        return (
          <div
            className={c('__confirmation-address-box')}
            data-testid="vowifi-confirmation-address"
          >
            <Stack className="gap-[3px]!" direction="column">
              {lines.filter(Boolean).map(line => {
                return (
                  <Paragraph key={line} fontFamily={themeValues.fontParagraph}>
                    {line}
                  </Paragraph>
                );
              })}
            </Stack>
          </div>
        );
      }

      return (
        <div className={c('__confirmation-screen-body')}>
          <Stack className="gap-[3px]!" direction="column">
            {!!streetLine && (
              <Paragraph fontFamily={themeValues.fontParagraph}>
                {streetLine}
              </Paragraph>
            )}
            {!!formState.address_line_2 && (
              <Paragraph fontFamily={themeValues.fontParagraph}>
                {formState.address_line_2}
              </Paragraph>
            )}
            {!!formState.city && (
              <Paragraph fontFamily={themeValues.fontParagraph}>
                {formState.city}
              </Paragraph>
            )}
            {!!formState.state_province_region && (
              <Paragraph fontFamily={themeValues.fontParagraph}>
                {formState.state_province_region}
              </Paragraph>
            )}
            {!!formState.zip_postal_code && (
              <Paragraph fontFamily={themeValues.fontParagraph}>
                {formState.zip_postal_code}
              </Paragraph>
            )}
          </Stack>
        </div>
      );
    }

    return null;
  };

  const renderIconStatus = () => {
    const iconMap = {
      [statusTypes.pending]: (
        <IconRenderer
          variant="spinner"
          className={c('__status-icon')}
          app="websheet"
        />
      ),
      [statusTypes.success]: (
        <IconRenderer
          variant="success"
          className={c('__status-icon')}
          app="websheet"
        />
      ),
      [statusTypes.error]: (
        <IconRenderer
          variant="error"
          className={c('__status-icon', '__status-icon--error')}
          app="websheet"
        />
      ),
    } as const;

    return iconMap[status] ?? null;
  };

  return (
    <div ref={containerRef} className={c('__confirmation-screen')}>
      <Stack gap={6} direction="column" justify="start">
        <HeaderImage
          src={themeValues.logoUrl}
          isPortalPreview={isPortalPreview}
          onError={() => onImageError(themeValues.logoUrl)}
        />
        {/* New flow (Figma 2454-929): the status icon sits ABOVE the title
            and the error screen has no icon. Legacy keeps title-then-icon. */}
        {autocompleteEnabled && status !== statusTypes.error && (
          <div className={c('__icon-wrapper')}>{renderIconStatus()}</div>
        )}
        {/* Status testid: brand copy is arbitrary (localised titles), so e2e
            asserts the terminal screen structurally, not by text. */}
        <Title
          fontFamily={themeValues.fontTitle}
          data-testid={
            status ? `vowifi-status-${status}` : 'vowifi-confirmation-title'
          }
        >
          {renderPageTitle()}
        </Title>

        {!autocompleteEnabled && (
          <div className={c('__icon-wrapper')}>{renderIconStatus()}</div>
        )}

        {renderSubTitle()}

        {!status && renderBody()}

        <ActionSection
          themeValues={themeValues}
          errorMessage={errorMessage}
          isAddressError={isAddressError}
          context={status === 'pending' ? 'loading' : status}
          isPortalPreview={isPortalPreview}
          confirm={confirm}
          editAddress={editAddress}
          close={close}
          done={done}
          isLoading={isLoading}
          status={status}
          handleSubmit={handleSubmit}
          deviceSdk={deviceSdk}
          autocompleteEnabled={autocompleteEnabled}
          defaultTexts={
            autocompleteEnabled
              ? { ...defaultTexts, done: 'Close' }
              : defaultTexts
          }
        />
      </Stack>
    </div>
  );
};
